import { useQueryClient } from '@tanstack/react-query';
import { useDependency } from 'ditox-react';
import { produce } from 'immer';
import { useSubscription } from 'observable-hooks';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import useMessageHistory from '@/hooks/conversations/use-message-history';
import useMessageWebSocketDispatch from '@/hooks/conversations/use-message-web-socket-dispatch';
import { useIsUploadingFile } from '@/hooks/files/use-upload-file';
import useGroupInfo, { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import useSession from '@/hooks/sessions/use-session';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationGroupType } from '@/models/conversations/conversation-session';
import { getUserId } from '@/stores/auth';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { generateSessionKey } from '@/utils/session';

export function useGroupMessages(groupId?: Nullable<number>, searchId?: number) {
  const queryClient = useQueryClient();
  const { data: groupInfo, isLoading: isGroupInfoLoading, error: groupInfoError } = useGroupInfo(groupId);
  const { isLoading: isHistoryLoading, error: historyError } = useMessageHistory('group', groupId, searchId);
  const sessionKey = generateSessionKey('group', groupId);
  const { session } = useSession<ConversationGroupType>('group', groupId);

  const lastReadNotice = useConversationSessionStore((s) => s.lastReadNotices[sessionKey]);
  const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);
  const isUploadingFile = useIsUploadingFile();

  useSubscription(webSocketManager.event$, ({ action, params }) => {
    switch (action) {
      case 'message':
        if (params.message.UserID === getUserId()) {
          useConversationSessionStore
            .getState()
            .setLastRead(generateSessionKey('group', groupId), params.message.ID)
            .catch(console.error);
        }
        if (params.type === 'groupChat' && params.message.MsgType === ConversationMessageContentType.Notice) {
          const announcement = params.message.GroupInfo ?? params.message.Message;
          queryClient.setQueryData(generateGroupInfoQueryOptions(groupId).queryKey, (data) =>
            produce(data, (draft) => {
              if (!draft?.GroupData) return draft;

              draft.GroupData.Info = announcement;

              return draft;
            }),
          );
        }
        break;
    }
  });

  return {
    group: groupInfo?.GroupData,
    session,
    hasUnreadNotice: groupInfo?.GroupData?.Info && groupInfo.GroupData.Info !== lastReadNotice,
    isHistoryLoading,
    isLoading: isUploadingFile || isHistoryLoading || isGroupInfoLoading,
    error: groupInfoError ?? historyError,
  } as const;
}
